import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import {
        FiSave,
        FiTrash2,
        FiEdit2,
        FiLoader
} from 'react-icons/fi';
import { apiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import PeriodicitySettings from '../../../components/PeriodicitySettings';
import type { 
        RosterSession, 
        RosterSessionType, 
        WeeklySchedule, 
        DayOfWeek,
        SessionModalState,
        SessionFormData,
        RecurrencePattern,
        DraggableSessionTypeProps,
        SessionProps,
        DayScheduleProps,
        SessionModalProps
} from '../../../types/rostering';
import type { Provider, Room, Facility } from '../../../types';

interface TimeSelectProps {
        value: string;
        onChange: (value: string) => void;
}

const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange }) => {
        return (
                <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="select select-bordered w-full"
                >
                        {Array.from({ length: 48 }, (_, i) => {
                                const hour = Math.floor(i / 2).toString().padStart(2, '0');
                                const minute = i % 2 === 0 ? '00' : '30';
                                const timeValue = `${hour}:${minute}`;
                                return (
                                        <option key={timeValue} value={timeValue}>
                                                {timeValue}
                                        </option>
                                );
                        })}
                </select>
        );
};

interface SessionTypeSelectProps {
        value: RosterSessionType;
        onChange: (value: RosterSessionType) => void;
        sessionTypes: RosterSessionType[];
}

const SessionTypeSelect: React.FC<SessionTypeSelectProps> = ({ value, onChange, sessionTypes }) => {
        return (
                <select
                        value={value.id}
                        onChange={(e) => {
                                const selected = sessionTypes.find(t => t.id === parseInt(e.target.value));
                                if (selected) onChange(selected);
                        }}
                        className="select select-bordered w-full"
                >
                        {sessionTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                        {type.name}
                                </option>
                        ))}
                </select>
        );
};

const DraggableSessionType: React.FC<DraggableSessionTypeProps> = ({ type }) => {
        const { attributes, listeners, setNodeRef, transform } = useDraggable({
                id: `template-${type.id}`,
                data: { type }
        });

        const style = transform ? {
                transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
                cursor: 'grab'
        } : {
                cursor: 'grab'
        };

        return (
                <div
                        ref={setNodeRef}
                        {...listeners}
                        {...attributes}
                        className={`${type.color} p-2 rounded select-none touch-none`}
                        style={style}
                >
                        {type.name}
                </div>
        );
};

const Session: React.FC<SessionProps> = ({ session, onEdit, onDelete }) => {
        return (
                <div className={`${session.type.color} p-3 rounded group shadow-sm`}>
                        <div className="flex justify-between items-center">
                                <div>
                                        <span className="font-medium">{session.type.name}</span>
                                        {session.roomName && (
                                                <div className="text-xs opacity-70">{session.roomName}</div>
                                        )}
                                </div>
                                <div className="flex items-center gap-2">
                                        <button
                                                onClick={() => onEdit(session)}
                                                className="opacity-0 group-hover:opacity-100 btn btn-ghost btn-xs"
                                        >
                                                <FiEdit2 size={16} />
                                        </button>
                                        <button
                                                onClick={() => onDelete(session)}
                                                className="opacity-0 group-hover:opacity-100 btn btn-ghost btn-xs text-error"
                                        >
                                                <FiTrash2 size={16} />
                                        </button>
                                </div>
                        </div>
                        <div className="text-base-content opacity-80 text-sm mt-1">
                                {session.startTime} - {session.endTime}
                        </div>
                </div>
        );
};

const DaySchedule: React.FC<DayScheduleProps> = ({ day, sessions, onEditSession, onDeleteSession }) => {
        const { isOver, setNodeRef } = useDroppable({
                id: `droppable-${day}`,
                data: { day }
        });

        return (
                <div
                        ref={setNodeRef}
                        className={`card bg-base-100 border p-4 min-h-[200px] ${isOver ? 'border-primary border-2' : 'border-base-300'}`}
                >
                        <h3 className="font-semibold mb-2 text-base-content">{day}</h3>
                        <div className="space-y-2">
                                {sessions.map((session, index) => (
                                        <Session
                                                key={index}
                                                session={session}
                                                onEdit={onEditSession}
                                                onDelete={onDeleteSession}
                                        />
                                ))}
                        </div>
                </div>
        );
};

const SessionModal: React.FC<SessionModalProps> = ({ session, sessionType, day, facilityId, onSave, onClose }) => {
        const { token } = useAuth();
        const [sessionTypes, setSessionTypes] = useState<RosterSessionType[]>([]);
        const [rooms, setRooms] = useState<Room[]>([]);
        const [loading, setLoading] = useState(true);

        const [type, setType] = useState<RosterSessionType | null>(session?.type || sessionType || null);
        const [startTime, setStartTime] = useState(session?.startTime || '09:00');
        const [endTime, setEndTime] = useState(session?.endTime || '10:00');
        const [roomId, setRoomId] = useState<string>(session?.roomId || '');

        // Load session types and the facility's rooms from API
        useEffect(() => {
                const loadModalData = async () => {
                        try {
                                // Session types are facility-scoped — no facility, no types.
                                const [sessionTypesResponse, roomsResponse] = await Promise.all([
                                        facilityId
                                                ? apiCall(`/providers/session-types/?facility=${facilityId}`, { method: 'GET' }, token || undefined)
                                                : Promise.resolve(null),
                                        facilityId
                                                ? apiCall(`/facilities/rooms/?facility=${facilityId}`, { method: 'GET' }, token || undefined)
                                                : Promise.resolve(null),
                                ]);

                                if (sessionTypesResponse) {
                                        const data = await sessionTypesResponse.json();
                                        const items = data.results || data;

                                        // Transform API session types to RosterSessionType format
                                        const rosterTypes: RosterSessionType[] = Array.isArray(items)
                                                ? items.map((st: any) => ({
                                                        id: st.id,
                                                        name: st.name,
                                                        color: st.color || 'bg-gray-200',
                                                        description: st.description,
                                                        duration_minutes: st.duration_minutes
                                                }))
                                                : [];

                                        setSessionTypes(rosterTypes);

                                        // Set default type if not already set
                                        if (!type && rosterTypes.length > 0) {
                                                setType(rosterTypes[0]);
                                        }
                                } else {
                                        setSessionTypes([]);
                                }

                                if (roomsResponse) {
                                        const roomsData = await roomsResponse.json();
                                        const roomItems = roomsData.results || roomsData;
                                        setRooms(Array.isArray(roomItems) ? roomItems : []);
                                } else {
                                        setRooms([]);
                                }
                        } catch (err) {
                                console.error('Error loading session modal data:', err);
                        } finally {
                                setLoading(false);
                        }
                };

                loadModalData();
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [token, facilityId]);

        const handleSave = () => {
                if (!type) return;
                const selectedRoom = rooms.find(r => r.id === roomId);
                onSave({
                        type,
                        startTime,
                        endTime,
                        roomId: roomId || undefined,
                        roomName: selectedRoom?.name,
                });
                onClose();
        };

        if (loading) {
                return (
                        <div className="modal modal-open">
                                <div className="modal-box">
                                        <div className="flex items-center justify-center">
                                                <FiLoader className="animate-spin" size={24} />
                                        </div>
                                </div>
                        </div>
                );
        }

        return (
                <div className="modal modal-open">
                        <div className="modal-box">
                                <h3 className="text-lg font-semibold mb-4 text-base-content">
                                        {session ? 'Edit Session' : 'Add Session'}
                                        {day && <span className="text-sm font-normal opacity-60 ml-2">({day})</span>}
                                </h3>
                                <div className="space-y-4">
                                        <div className="form-control">
                                                <label className="label"><span className="label-text">Session Type</span></label>
                                                {type && sessionTypes.length > 0 && (
                                                        <SessionTypeSelect 
                                                                value={type} 
                                                                onChange={setType}
                                                                sessionTypes={sessionTypes}
                                                        />
                                                )}
                                        </div>
                                        <div className="flex gap-4">
                                                <div className="form-control flex-1">
                                                        <label className="label"><span className="label-text">Start Time</span></label>
                                                        <TimeSelect value={startTime} onChange={setStartTime} />
                                                </div>
                                                <div className="form-control flex-1">
                                                        <label className="label"><span className="label-text">End Time</span></label>
                                                        <TimeSelect value={endTime} onChange={setEndTime} />
                                                </div>
                                        </div>
                                        <div className="form-control">
                                                <label className="label"><span className="label-text">Room</span></label>
                                                <select
                                                        value={roomId}
                                                        onChange={(e) => setRoomId(e.target.value)}
                                                        className="select select-bordered w-full"
                                                        disabled={!facilityId}
                                                >
                                                        <option value="">
                                                                {facilityId ? 'No specific room' : 'Select a facility first'}
                                                        </option>
                                                        {rooms.map(room => (
                                                                <option key={room.id} value={room.id}>{room.name}</option>
                                                        ))}
                                                </select>
                                        </div>
                                        <div className="modal-action">
                                                <button
                                                        onClick={onClose}
                                                        className="btn"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        onClick={handleSave}
                                                        disabled={!type}
                                                        className="btn btn-primary"
                                                >
                                                        Save
                                                </button>
                                        </div>
                                </div>
                        </div>
                </div>
        );
};

const SessionRosterEditor: React.FC = () => {
        const { token } = useAuth();
        const [selectedProvider, setSelectedProvider] = useState<number | string>('');
        const [selectedFacility, setSelectedFacility] = useState<string>('');
        const [facilities, setFacilities] = useState<Facility[]>([]);
        const [showPeriodicity, setShowPeriodicity] = useState(false);
        const [periodicity, setPeriodicity] = useState<RecurrencePattern>({
                startDate: new Date().toISOString().slice(0, 10),
                frequency: 'never',
                interval: 1,
                weeklyDays: [1],
                monthMode: 'day_of_month',
                monthDay: 1,
                monthWeek: 1,
                monthWeekday: 1,
                yearMonth: 1,
                yearMode: 'day_of_month',
                yearDay: 1,
                yearWeek: 1,
                yearWeekday: 1,
                endMode: 'never',
                occurrences: 2,
                endDate: new Date().toISOString().slice(0, 10),
        });
        const [providers, setProviders] = useState<Provider[]>([]);
        const [sessionTypes, setSessionTypes] = useState<RosterSessionType[]>([]);
        const [weekSessions, setWeekSessions] = useState<WeeklySchedule>({
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
        });
        const [sessionModal, setSessionModal] = useState<SessionModalState>({
                show: false,
                day: null,
                session: null,
                sessionType: null
        });
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);

        // Load providers from API
        const loadProviders = useCallback(async () => {
                try {
                        const response = await apiCall('/providers/providers/', { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        setProviders(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading providers:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load providers');
                }
        }, [token]);

        // Load facilities from API
        const loadFacilities = useCallback(async () => {
                try {
                        const response = await apiCall('/facilities/facilities/', { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        setFacilities(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading facilities:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load facilities');
                }
        }, [token]);

        // Load session types from API — scoped to the selected facility
        const loadSessionTypes = useCallback(async () => {
                if (!selectedFacility) {
                        setSessionTypes([]);
                        return;
                }
                try {
                        const response = await apiCall(`/providers/session-types/?facility=${selectedFacility}`, { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        const items = data.results || data;
                        
                        // Transform API session types to RosterSessionType format
                        const rosterTypes: RosterSessionType[] = Array.isArray(items) 
                                ? items.map((st: any) => ({
                                        id: st.id,
                                        name: st.name,
                                        color: st.color || 'bg-gray-200',
                                        description: st.description,
                                        duration_minutes: st.duration_minutes
                                }))
                                : [];
                        
                        setSessionTypes(rosterTypes);
                } catch (err) {
                        console.error('Error loading session types:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load session types');
                }
        }, [token, selectedFacility]);

        // Initialize data
        useEffect(() => {
                const initializeData = async () => {
                        setLoading(true);
                        await Promise.all([loadProviders(), loadFacilities(), loadSessionTypes()]);
                        setLoading(false);
                };

                initializeData();
        }, [loadProviders, loadFacilities, loadSessionTypes]);

        // The backend has no "roster template" concept — a roster is just one or
        // more recurring ProviderSession rows. Each unique (type, room, start, end)
        // combination in the week becomes a single POST to /providers/sessions/,
        // with active_days listing every day that combination appears on; the
        // endpoint itself expands that into individual dated sessions server-side.
        const DAY_CODES: Record<DayOfWeek, string> = {
                Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'TH',
                Friday: 'F', Saturday: 'S', Sunday: 'SU',
        };

        const handleSaveTemplate = async () => {
                if (!selectedProvider) {
                        alert('Please select a provider first');
                        return;
                }
                if (!selectedFacility) {
                        alert('Please select a facility first');
                        return;
                }

                type SessionGroup = {
                        type: RosterSessionType;
                        startTime: string;
                        endTime: string;
                        roomId?: string;
                        days: DayOfWeek[];
                };
                const groups = new Map<string, SessionGroup>();

                (Object.keys(weekSessions) as DayOfWeek[]).forEach((day) => {
                        (weekSessions[day] || []).forEach((session) => {
                                const key = `${session.type.id}|${session.startTime}|${session.endTime}|${session.roomId || ''}`;
                                const existing = groups.get(key);
                                if (existing) {
                                        existing.days.push(day);
                                } else {
                                        groups.set(key, {
                                                type: session.type,
                                                startTime: session.startTime,
                                                endTime: session.endTime,
                                                roomId: session.roomId,
                                                days: [day],
                                        });
                                }
                        });
                });

                if (groups.size === 0) {
                        alert('Add at least one session to the week before saving');
                        return;
                }

                const isRecurring = periodicity.frequency !== 'never';
                const repeatUnit = !isRecurring ? 'once' : 'weeks';
                const repeatInterval = periodicity.frequency === 'weekly' ? periodicity.interval : 1;

                try {
                        for (const group of groups.values()) {
                                const payload = {
                                        provider: selectedProvider,
                                        facility: selectedFacility,
                                        room: group.roomId || undefined,
                                        session_type: group.type.id,
                                        session_start_time: group.startTime,
                                        session_end_time: group.endTime,
                                        active_days: group.days.map(d => DAY_CODES[d]).join(','),
                                        repeat_unit: repeatUnit,
                                        repeat_interval: repeatInterval,
                                        repeat_start_date: periodicity.startDate,
                                        repeat_end_date: periodicity.endMode === 'on' ? periodicity.endDate : undefined,
                                        repeat_indefinite: periodicity.endMode === 'never' && isRecurring,
                                };

                                const response = await apiCall('/providers/sessions/', {
                                        method: 'POST',
                                        body: JSON.stringify(payload),
                                }, token || undefined);

                                if (!response.ok) {
                                        const errorBody = await response.json().catch(() => ({}));
                                        throw new Error(errorBody.detail || errorBody.error || 'Failed to save one or more sessions');
                                }
                        }

                        alert('Roster saved successfully!');
                } catch (err) {
                        console.error('Error saving roster:', err);
                        alert(err instanceof Error ? err.message : 'Failed to save roster. Please try again.');
                }
        };

        const handleDragEnd = (event: any) => {
                const { over, active } = event;

                if (over && active.data.current) {
                        const day = over.data.current.day as DayOfWeek;
                        const sessionType = active.data.current.type as RosterSessionType;

                        if (day && sessionType) {
                                setSessionModal({
                                        show: true,
                                        day,
                                        session: null,
                                        sessionType
                                });
                        }
                }
        };

        const handleEditSession = (day: DayOfWeek, session: RosterSession) => {
                setSessionModal({ show: true, day, session, sessionType: session.type });
        };

        const handleDeleteSession = (day: DayOfWeek, session: RosterSession) => {
                const currentSessions = weekSessions[day] || [];
                const updatedSessions = {
                        ...weekSessions,
                        [day]: currentSessions.filter(s => s !== session)
                };
                setWeekSessions(updatedSessions);
        };

        const checkTimeOverlap = (sessions: RosterSession[], newSession: SessionFormData, editingSession: RosterSession | null = null): boolean => {
                const newStart = parseInt(newSession.startTime.split(':')[0]) * 60 + parseInt(newSession.startTime.split(':')[1]);
                const newEnd = parseInt(newSession.endTime.split(':')[0]) * 60 + parseInt(newSession.endTime.split(':')[1]);

                return sessions.some(session => {
                        if (session === editingSession) return false;

                        const existingStart = parseInt(session.startTime.split(':')[0]) * 60 + parseInt(session.startTime.split(':')[1]);
                        const existingEnd = parseInt(session.endTime.split(':')[0]) * 60 + parseInt(session.endTime.split(':')[1]);

                        return (newStart < existingEnd && newEnd > existingStart);
                });
        };

        const sortSessionsByTime = (sessions: RosterSession[]): RosterSession[] => {
                return [...sessions].sort((a, b) => {
                        const timeA = parseInt(a.startTime.split(':')[0]) * 60 + parseInt(a.startTime.split(':')[1]);
                        const timeB = parseInt(b.startTime.split(':')[0]) * 60 + parseInt(b.startTime.split(':')[1]);
                        return timeA - timeB;
                });
        };

        const handleSaveSession = (sessionData: SessionFormData) => {
                if (!sessionModal.day) return;

                // Validate that end time is after start time
                const startMinutes = parseInt(sessionData.startTime.split(':')[0]) * 60 + parseInt(sessionData.startTime.split(':')[1]);
                const endMinutes = parseInt(sessionData.endTime.split(':')[0]) * 60 + parseInt(sessionData.endTime.split(':')[1]);

                if (endMinutes <= startMinutes) {
                        alert('End time must be after start time');
                        return;
                }

                const currentDaySessions = weekSessions[sessionModal.day] || [];

                // Check for time overlaps
                const hasOverlap = checkTimeOverlap(
                        currentDaySessions,
                        sessionData,
                        sessionModal.session
                );

                if (hasOverlap) {
                        alert('This time slot overlaps with an existing session');
                        return;
                }

                const newSession: RosterSession = {
                        type: sessionData.type,
                        startTime: sessionData.startTime,
                        endTime: sessionData.endTime,
                        day: sessionModal.day,
                        notes: sessionData.notes,
                        roomId: sessionData.roomId,
                        roomName: sessionData.roomName,
                };

                const updatedSessions = {
                        ...weekSessions,
                        [sessionModal.day]: sortSessionsByTime(
                                sessionModal.session
                                        ? currentDaySessions.map(s =>
                                                s === sessionModal.session ? newSession : s)
                                        : [...currentDaySessions, newSession]
                        )
                };
                setWeekSessions(updatedSessions);
                setSessionModal({
                        show: false,
                        day: null,
                        session: null,
                        sessionType: null
                });
        };

        if (loading) {
                return (
                        <div className="container-fluid px-8 py-6">
                                <div className="flex items-center justify-center py-12">
                                        <FiLoader className="animate-spin" size={32} />
                                        <span className="ml-3 text-lg">Loading roster editor...</span>
                                </div>
                        </div>
                );
        }

        if (error) {
                return (
                        <div className="container-fluid px-8 py-6">
                                <div className="alert alert-error">
                                        <span>Error: {error}</span>
                                </div>
                        </div>
                );
        }

        return (
                <DndContext onDragEnd={handleDragEnd}>
                        <div className="container-fluid px-8 py-6">
                                <div className="mb-6 space-y-4">
                                        <div className="flex justify-between items-center">
                                                <h1 className="text-2xl font-bold text-base-content">Session Roster Editor</h1>
                                                <div className="flex gap-4">
                                                        <button
                                                                onClick={handleSaveTemplate}
                                                                disabled={!selectedProvider || !selectedFacility || Object.values(weekSessions).every(day => day.length === 0)}
                                                                className="btn btn-primary gap-2"
                                                        >
                                                                <FiSave size={16} />
                                                                Save Roster
                                                        </button>
                                                </div>
                                        </div>

                                        <div className="flex gap-4 items-center">
                                                <select
                                                        value={selectedProvider}
                                                        onChange={(e) => setSelectedProvider(e.target.value)}
                                                        className="select select-bordered w-64"
                                                >
                                                        <option value="">Select Provider</option>
                                                        {providers.map(provider => (
                                                                <option key={provider.id} value={provider.id}>
                                                                        {provider.full_name || `${provider.first_name} ${provider.last_name}`}
                                                                </option>
                                                        ))}
                                                </select>

                                                <select
                                                        value={selectedFacility}
                                                        onChange={(e) => setSelectedFacility(e.target.value)}
                                                        className="select select-bordered w-64"
                                                >
                                                        <option value="">Select Facility</option>
                                                        {facilities.map(facility => (
                                                                <option key={facility.id} value={facility.id}>
                                                                        {facility.name}
                                                                </option>
                                                        ))}
                                                </select>

                                                <button
                                                        className="btn btn-outline"
                                                        onClick={() => setShowPeriodicity(prev => !prev)}
                                                >
                                                        {showPeriodicity ? 'Hide Periodicity' : 'Set Repeat Pattern'}
                                                </button>
                                        </div>

                                        {showPeriodicity && (
                                                <PeriodicitySettings
                                                        value={periodicity}
                                                        onChange={setPeriodicity}
                                                        includeStartDate={true}
                                                        includeEndDateOption={true}
                                                />
                                        )}
                                </div>

                                <div className="grid grid-cols-7 gap-6">
                                        <div className="col-span-1">
                                                <div className="card bg-base-100 border border-base-300 shadow-sm">
                                                        <div className="card-body p-4">
                                                                <h3 className="font-semibold mb-2 text-base-content">Session Types</h3>
                                                                <div className="space-y-2">
                                                                        {sessionTypes.map(type => (
                                                                                <DraggableSessionType key={type.id} type={type} />
                                                                        ))}
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <div className="col-span-6">
                                                <div className="grid grid-cols-5 gap-6">
                                                        {(Object.keys(weekSessions) as DayOfWeek[]).map((day) => (
                                                                <DaySchedule
                                                                        key={day}
                                                                        day={day}
                                                                        sessions={weekSessions[day] || []}
                                                                        onEditSession={(session) => handleEditSession(day, session)}
                                                                        onDeleteSession={(session) => handleDeleteSession(day, session)}
                                                                />
                                                        ))}
                                                </div>
                                        </div>
                                </div>

                                {sessionModal.show && (
                                        <SessionModal
                                                session={sessionModal.session}
                                                sessionType={sessionModal.sessionType}
                                                day={sessionModal.day}
                                                facilityId={selectedFacility || null}
                                                onSave={handleSaveSession}
                                                onClose={() => setSessionModal({
                                                        show: false,
                                                        day: null,
                                                        session: null,
                                                        sessionType: null
                                                })}
                                        />
                                )}
                        </div>
                </DndContext>
        );
};

export default SessionRosterEditor;